import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { useAudience } from '../context/AudienceContext';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Switch } from '../components/ui/switch';
import { toast } from 'sonner';
import {
  Pill,
  Search,
  Ear,
  Wind,
  Droplet,
  Hand,
  Utensils,
  Heart,
  Bone,
  Brain,
  ChevronRight,
  ChevronDown,
  Clock,
  AlertTriangle,
  Copy,
  Check,
  Loader2,
  Info,
  Sparkles,
  X
} from 'lucide-react';

import { API_URL } from '../config';

const categoryIcons = {
  orl: Ear,
  respiratoire: Wind,
  urinaire: Droplet,
  cutane: Hand,
  digestif: Utensils,
  ist: Heart,
  vulvovaginite: Heart,
  'osteo-articulaire': Bone,
  'neuro-meninge': Brain,
};

const categoryColors = {
  orl: 'text-purple-400 bg-purple-400/10 border-purple-400/30',
  respiratoire: 'text-cyan-400 bg-cyan-400/10 border-cyan-400/30',
  urinaire: 'text-blue-400 bg-blue-400/10 border-blue-400/30',
  cutane: 'text-orange-400 bg-orange-400/10 border-orange-400/30',
  digestif: 'text-green-400 bg-green-400/10 border-green-400/30',
  ist: 'text-pink-400 bg-pink-400/10 border-pink-400/30',
  vulvovaginite: 'text-pink-400 bg-pink-400/10 border-pink-400/30',
  'osteo-articulaire': 'text-amber-400 bg-amber-400/10 border-amber-400/30',
  'neuro-meninge': 'text-red-400 bg-red-400/10 border-red-400/30',
};

export default function AntibioPage() {
  const { token } = useAuth();
  const { audience, isAdult } = useAudience();
  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [infections, setInfections] = useState([]);
  const [selectedGuide, setSelectedGuide] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [allergies, setAllergies] = useState([]);
  const [currentAllergy, setCurrentAllergy] = useState('');
  const [showAllergyFilter, setShowAllergyFilter] = useState(false);
  const [aiRecommendation, setAiRecommendation] = useState(null);
  const [aiLoading, setAiLoading] = useState(false);

  useEffect(() => {
    fetchCategories();
  }, [audience]);

  useEffect(() => {
    if (selectedCategory) {
      fetchInfections(selectedCategory);
    }
  }, [selectedCategory, audience]);

  const fetchCategories = async () => {
    try {
      const response = await fetch(`${API_URL}/api/antibio/categories?audience=${audience}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setCategories(data.categories || []);
        // Reset selected category if it doesn't exist in new categories
        if (selectedCategory) {
          const categoryExists = data.categories?.some(c => c.id === selectedCategory);
          if (!categoryExists) {
            setSelectedCategory(null);
            setInfections([]);
          }
        }
      }
    } catch (error) {
      console.error('Error fetching categories:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchInfections = async (category) => {
    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/antibio/infections/${category}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        // Filter by audience
        const filtered = data.infections?.filter(i => i.audience === audience) || [];
        setInfections(filtered);
      }
    } catch (error) {
      console.error('Error fetching infections:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = async (query) => {
    setSearchQuery(query);
    if (query.length < 2) {
      setSearchResults([]);
      return;
    }

    try {
      const response = await fetch(`${API_URL}/api/antibio/search?query=${encodeURIComponent(query)}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        const filtered = data.guides?.filter(g => g.audience === audience) || [];
        setSearchResults(filtered);
      }
    } catch (error) {
      console.error('Error searching:', error);
    }
  };

  const selectGuide = (guide) => {
    setSelectedGuide(guide);
    setSearchQuery('');
    setSearchResults([]);
    setAiRecommendation(null);
  };

  const addAllergy = () => {
    if (currentAllergy.trim() && !allergies.includes(currentAllergy.trim())) {
      setAllergies([...allergies, currentAllergy.trim()]);
      setCurrentAllergy('');
    }
  };

  const removeAllergy = (allergy) => {
    setAllergies(allergies.filter(a => a !== allergy));
  };

  const getAIRecommendation = async () => {
    if (!selectedGuide) return;
    
    setAiLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/antibio/recommend`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          infection_type: selectedGuide.category,
          infection_name: selectedGuide.name,
          patient_type: audience,
          allergies: allergies
        })
      });

      if (response.ok) {
        const data = await response.json();
        setAiRecommendation(data);
      }
    } catch (error) {
      console.error('Error getting AI recommendation:', error);
      toast.error('Erreur lors de la recommandation');
    } finally {
      setAiLoading(false);
    }
  };

  const copyTreatment = (treatment) => {
    const text = `${treatment.antibiotic} ${treatment.dose} ${treatment.frequency} - ${treatment.duration} (${treatment.route})`;
    navigator.clipboard.writeText(text);
    toast.success('Posologie copiée');
  };

  const getFilteredTreatments = () => {
    if (!selectedGuide?.treatments) return [];
    
    let treatments = [...selectedGuide.treatments];
    
    // Filter based on allergies
    if (allergies.length > 0) {
      const hasBetaLactamAllergy = allergies.some(a => 
        a.toLowerCase().includes('pénicilline') || 
        a.toLowerCase().includes('penicilline') ||
        a.toLowerCase().includes('bêtalactamine') ||
        a.toLowerCase().includes('betalactamine')
      );
      
      if (hasBetaLactamAllergy) {
        treatments = treatments.filter(t => !t.is_betalactam);
      }
    }
    
    return treatments;
  };

  return (
    <div className="p-4 md:p-6 space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold flex items-center gap-2">
          <Pill className="w-6 h-6 text-cyan-400" />
          Antibioguide
        </h1>
        <p className="text-[hsl(var(--text-dim))]">
          Guide d'antibiothérapie {isAdult ? 'adulte' : 'pédiatrique'} - Recommandations 2024
        </p>
      </div>

      {/* Search */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[hsl(var(--muted))]" />
        <Input
          type="text"
          value={searchQuery}
          onChange={(e) => handleSearch(e.target.value)}
          placeholder="Rechercher une infection ou un antibiotique..."
          className="pl-10 bg-[hsl(var(--bg-elev))]"
          data-testid="antibio-search"
        />
        
        {/* Search Results */}
        {searchResults.length > 0 && (
          <div className="absolute top-full left-0 right-0 mt-2 bg-[hsl(var(--bg-elev))] border border-[hsl(var(--border))] rounded-lg shadow-xl z-50 max-h-64 overflow-y-auto">
            {searchResults.map((guide) => {
              const Icon = categoryIcons[guide.category] || Pill;
              return (
                <button
                  key={guide._id}
                  onClick={() => selectGuide(guide)}
                  className="w-full text-left p-3 hover:bg-cyan-400/10 transition-colors border-b border-[hsl(var(--border))] last:border-0"
                >
                  <div className="flex items-center gap-2">
                    <Icon className="w-4 h-4 text-cyan-400" />
                    <span className="font-medium">{guide.name}</span>
                  </div>
                  <p className="text-xs text-[hsl(var(--text-dim))] mt-1">{guide.description}</p>
                </button>
              );
            })}
          </div>
        )}
      </div>

      {/* Allergy Filter */}
      <div className="glass-card rounded-xl p-4">
        <button 
          onClick={() => setShowAllergyFilter(!showAllergyFilter)}
          className="flex items-center justify-between w-full"
        >
          <div className="flex items-center gap-2">
            <AlertTriangle className="w-4 h-4 text-amber-400" />
            <span className="font-medium">Allergies connues</span>
            {allergies.length > 0 && (
              <Badge className="bg-amber-400/10 text-amber-400 border-amber-400/30">
                {allergies.length}
              </Badge>
            )}
          </div>
          {showAllergyFilter ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
        </button>
        
        {showAllergyFilter && (
          <div className="mt-4 space-y-3">
            <div className="flex gap-2">
              <Input
                value={currentAllergy}
                onChange={(e) => setCurrentAllergy(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && addAllergy()}
                placeholder="Ex: Pénicilline, Bêtalactamines..."
                className="flex-1 bg-transparent"
              />
              <Button onClick={addAllergy} variant="outline" size="sm">
                Ajouter
              </Button>
            </div>
            <div className="flex flex-wrap gap-2">
              {allergies.map((allergy, i) => (
                <Badge key={i} className="bg-amber-400/10 text-amber-400 border-amber-400/30">
                  {allergy}
                  <button onClick={() => removeAllergy(allergy)} className="ml-2">
                    <X className="w-3 h-3" />
                  </button>
                </Badge>
              ))}
            </div>
            <p className="text-xs text-[hsl(var(--text-dim))]">
              Les antibiotiques contre-indiqués seront automatiquement filtrés
            </p>
          </div>
        )}
      </div>

      {/* Main Content */}
      <div className="grid md:grid-cols-3 gap-6">
        {/* Categories */}
        <div className="space-y-2">
          <h2 className="font-semibold mb-3">Catégories</h2>
          {loading && !categories.length ? (
            <div className="space-y-2">
              {[1, 2, 3, 4].map(i => <div key={i} className="skeleton h-12 rounded-lg"></div>)}
            </div>
          ) : (
            categories.map((cat) => {
              const Icon = categoryIcons[cat.id] || Pill;
              const colors = categoryColors[cat.id] || 'text-cyan-400 bg-cyan-400/10 border-cyan-400/30';
              const isSelected = selectedCategory === cat.id;
              
              return (
                <button
                  key={cat.id}
                  onClick={() => {
                    setSelectedCategory(cat.id);
                    setSelectedGuide(null);
                  }}
                  className={`w-full text-left p-3 rounded-lg border transition-all ${
                    isSelected 
                      ? `${colors} ring-1 ring-current` 
                      : 'bg-[hsl(var(--bg-elev))] border-[hsl(var(--border))] hover:border-cyan-400/40'
                  }`}
                  data-testid={`antibio-category-${cat.id}`}
                >
                  <div className="flex items-center gap-3">
                    <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${isSelected ? '' : 'bg-cyan-400/10'}`}>
                      <Icon className={`w-4 h-4 ${isSelected ? '' : 'text-cyan-400'}`} />
                    </div>
                    <div>
                      <p className="font-medium text-sm">{cat.name}</p>
                      <p className="text-xs text-[hsl(var(--text-dim))]">{cat.description}</p>
                    </div>
                  </div>
                </button>
              );
            })
          )}
        </div>

        {/* Infections List */}
        <div className="space-y-2">
          <h2 className="font-semibold mb-3">
            {selectedCategory ? `Infections ${categories.find(c => c.id === selectedCategory)?.name || ''}` : 'Sélectionnez une catégorie'}
          </h2>
          {selectedCategory ? (
            infections.length > 0 ? (
              infections.map((infection) => (
                <button
                  key={infection._id}
                  onClick={() => selectGuide(infection)}
                  className={`w-full text-left p-3 rounded-lg border transition-all ${
                    selectedGuide?._id === infection._id
                      ? 'bg-cyan-400/10 border-cyan-400/40 ring-1 ring-cyan-400/30'
                      : 'bg-[hsl(var(--bg-elev))] border-[hsl(var(--border))] hover:border-cyan-400/40'
                  }`}
                  data-testid="antibio-infection-item"
                >
                  <p className="font-medium text-sm">{infection.name}</p>
                  <p className="text-xs text-[hsl(var(--text-dim))] mt-1">{infection.description}</p>
                  <div className="flex items-center gap-2 mt-2">
                    <Clock className="w-3 h-3 text-[hsl(var(--muted))]" />
                    <span className="text-xs text-[hsl(var(--text-dim))]">{infection.duration}</span>
                  </div>
                </button>
              ))
            ) : (
              <div className="text-center py-8">
                <Info className="w-8 h-8 text-[hsl(var(--muted))] mx-auto mb-2" />
                <p className="text-sm text-[hsl(var(--text-dim))]">
                  Aucune recommandation {isAdult ? 'adulte' : 'pédiatrique'} pour cette catégorie
                </p>
              </div>
            )
          ) : (
            <div className="text-center py-8">
              <Pill className="w-8 h-8 text-[hsl(var(--muted))] mx-auto mb-2" />
              <p className="text-sm text-[hsl(var(--text-dim))]">
                Sélectionnez une catégorie pour voir les infections
              </p>
            </div>
          )}
        </div>

        {/* Treatment Details */}
        <div className="space-y-4">
          <h2 className="font-semibold mb-3">Recommandations</h2>
          {selectedGuide ? (
            <div className="space-y-4">
              {/* Guide Header */}
              <div className="glass-card rounded-xl p-4 neon-border">
                <h3 className="font-semibold text-lg text-cyan-300">{selectedGuide.name}</h3>
                <p className="text-sm text-[hsl(var(--text-dim))] mt-1">{selectedGuide.description}</p>
                <div className="flex items-center gap-4 mt-3">
                  <Badge className="bg-cyan-400/10 text-cyan-300 border-cyan-400/30">
                    <Clock className="w-3 h-3 mr-1" />
                    {selectedGuide.duration}
                  </Badge>
                  <Badge variant="outline" className="text-[hsl(var(--text-dim))]">
                    {isAdult ? 'Adulte' : 'Pédiatrique'}
                  </Badge>
                </div>
              </div>

              {/* Treatments */}
              <div className="space-y-3">
                {getFilteredTreatments().map((treatment, i) => (
                  <div 
                    key={i} 
                    className={`glass-card rounded-lg p-4 border ${
                      treatment.line === 1 
                        ? 'border-green-500/30' 
                        : treatment.line === 2 
                          ? 'border-amber-500/30' 
                          : 'border-[hsl(var(--border))]'
                    }`}
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-2">
                          <Badge 
                            className={
                              treatment.line === 1 
                                ? 'bg-green-400/10 text-green-400 border-green-400/30' 
                                : treatment.line === 2 
                                  ? 'bg-amber-400/10 text-amber-400 border-amber-400/30'
                                  : 'bg-[hsl(var(--bg))] text-[hsl(var(--text-dim))]'
                            }
                          >
                            {treatment.line === 1 ? '1ère intention' : treatment.line === 2 ? '2ème intention' : 'Alternative'}
                          </Badge>
                        </div>
                        <h4 className="font-semibold text-cyan-300">{treatment.antibiotic}</h4>
                        <div className="mt-2 space-y-1">
                          <p className="text-sm font-mono">
                            <span className="text-[hsl(var(--text-dim))]">Dose:</span>{' '}
                            <span className="text-cyan-300">{treatment.dose}</span>
                          </p>
                          <p className="text-sm font-mono">
                            <span className="text-[hsl(var(--text-dim))]">Fréquence:</span>{' '}
                            <span className="text-cyan-300">{treatment.frequency}</span>
                          </p>
                          <p className="text-sm font-mono">
                            <span className="text-[hsl(var(--text-dim))]">Voie:</span>{' '}
                            <span className="text-cyan-300">{treatment.route}</span>
                          </p>
                          <p className="text-sm font-mono">
                            <span className="text-[hsl(var(--text-dim))]">Durée:</span>{' '}
                            <span className="text-cyan-300">{treatment.duration}</span>
                          </p>
                        </div>
                        {treatment.notes && (
                          <p className="text-xs text-[hsl(var(--text-dim))] mt-2 italic">
                            {treatment.notes}
                          </p>
                        )}
                      </div>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => copyTreatment(treatment)}
                        data-testid="antibio-copy-button"
                      >
                        <Copy className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                ))}

                {getFilteredTreatments().length === 0 && (
                  <div className="glass-card rounded-lg p-4 border-red-500/30 text-center">
                    <AlertTriangle className="w-8 h-8 text-red-400 mx-auto mb-2" />
                    <p className="text-sm text-red-400">
                      Aucun antibiotique compatible avec les allergies déclarées
                    </p>
                    <p className="text-xs text-[hsl(var(--text-dim))] mt-1">
                      Consultez un avis spécialisé
                    </p>
                  </div>
                )}
              </div>

              {/* Notes */}
              {selectedGuide.notes?.length > 0 && (
                <div className="glass-card rounded-lg p-4 border-amber-500/30">
                  <div className="flex items-center gap-2 text-amber-400 mb-2">
                    <Info className="w-4 h-4" />
                    <span className="font-medium text-sm">Notes importantes</span>
                  </div>
                  <ul className="space-y-1">
                    {selectedGuide.notes.map((note, i) => (
                      <li key={i} className="text-sm text-[hsl(var(--text-dim))] flex items-start gap-2">
                        <span className="text-amber-400">•</span>
                        {note}
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Surveillance */}
              {selectedGuide.surveillance?.length > 0 && (
                <div className="glass-card rounded-lg p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <Check className="w-4 h-4 text-cyan-400" />
                    <span className="font-medium text-sm">Surveillance</span>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {selectedGuide.surveillance.map((item, i) => (
                      <Badge key={i} variant="outline" className="text-cyan-300 border-cyan-400/30">
                        {item}
                      </Badge>
                    ))}
                  </div>
                </div>
              )}

              {/* AI Recommendation Button */}
              <Button
                onClick={getAIRecommendation}
                disabled={aiLoading}
                className="w-full bg-gradient-to-r from-cyan-400/80 to-blue-400/80 text-black font-medium hover:opacity-90"
                data-testid="antibio-ai-recommend"
              >
                {aiLoading ? (
                  <Loader2 className="w-4 h-4 animate-spin mr-2" />
                ) : (
                  <Sparkles className="w-4 h-4 mr-2" />
                )}
                Obtenir une recommandation IA personnalisée
              </Button>

              {/* AI Recommendation */}
              {aiRecommendation?.ai_response && (
                <div className="glass-card rounded-xl p-4 border-cyan-400/30 animate-scale-in">
                  <div className="flex items-center gap-2 text-cyan-400 mb-3">
                    <Brain className="w-5 h-5" />
                    <span className="font-medium">Recommandation IA</span>
                  </div>
                  <div className="prose prose-invert prose-sm max-w-none">
                    <pre className="whitespace-pre-wrap text-sm font-sans text-[hsl(var(--text))]">
                      {aiRecommendation.ai_response}
                    </pre>
                  </div>
                </div>
              )}
            </div>
          ) : (
            <div className="glass-card rounded-xl p-8 text-center">
              <Pill className="w-12 h-12 text-[hsl(var(--muted))] mx-auto mb-4" />
              <p className="text-[hsl(var(--text-dim))]">
                Sélectionnez une infection pour voir les recommandations d'antibiothérapie
              </p>
            </div>
          )}
        </div>
      </div>

      {/* Disclaimer */}
      <div className="glass-card rounded-lg p-4 border border-amber-500/30">
        <div className="flex items-start gap-3">
          <AlertTriangle className="w-5 h-5 text-amber-400 flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-sm font-medium text-amber-400">Avertissement</p>
            <p className="text-xs text-[hsl(var(--text-dim))] mt-1">
              Ces recommandations sont basées sur les guidelines françaises 2024 (SPILF, HAS, GPIP). 
              Elles constituent une aide à la prescription et ne se substituent pas au jugement clinique. 
              Adaptez toujours le traitement au contexte local et aux particularités du patient.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
